import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { Message } from 'primereact/message';
import { Menubar } from 'primereact/menubar';
import { confirmDialog } from 'primereact/confirmdialog';
import { allocationsApi, AllocationSuggestion } from '../../api';
import { useAccountPreference, useAccountMenuItem } from '../../hooks';

interface AllocationControlsProps {
  clientId: string;
  initialAmountTolerance?: number;
  initialDateWindow?: number;
  initialMaxClusterSize?: number;
  onSuggestionsChange: (suggestions: AllocationSuggestion[]) => void;
  onParamsChange: (params: {
    amountTolerance: number;
    dateWindow: number;
    maxClusterSize: number;
  }) => void;
  onRefresh: () => Promise<void>;
  onSwitchToSuggestions: () => void;
  onError: (message: string | null) => void;
}

export interface AllocationControlsHandle {
  runAllocations: () => Promise<void>;
}

const AllocationControls = forwardRef<AllocationControlsHandle, AllocationControlsProps>(function AllocationControls(
{
  clientId,
  initialAmountTolerance = 1.0,
  initialDateWindow = 3,
  initialMaxClusterSize = 3,
  onSuggestionsChange,
  onParamsChange,
  onRefresh,
  onSwitchToSuggestions,
  onError,
}: AllocationControlsProps,
ref
) {
  const [amountTolerance, setAmountTolerance] = useState<number>(initialAmountTolerance);
  const [dateWindow, setDateWindow] = useState<number>(initialDateWindow);
  const [maxClusterSize, setMaxClusterSize] = useState<number>(initialMaxClusterSize);
  const [isRunning, setIsRunning] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const { selectedAccountId } = useAccountPreference(clientId);
  const { accountMenuItem } = useAccountMenuItem(clientId);

  useEffect(() => {
    onParamsChange({
      amountTolerance,
      dateWindow,
      maxClusterSize,
    });
  }, [amountTolerance, dateWindow, maxClusterSize, onParamsChange]);

  const runAllocations = async () => {
    setIsRunning(true);
    setLocalError(null);
    onError(null);
    onSuggestionsChange([]);

    try {
      const result = await allocationsApi.runAllocationEngine(clientId, {
        amount_tolerance: amountTolerance,
        date_window: dateWindow,
        max_cluster_size: maxClusterSize,
        account_ids: selectedAccountId ? [selectedAccountId] : undefined,
      });
      onSuggestionsChange(result.suggestions);

      await onRefresh();

      // Show results dialog
      const totalFound = result.allocations_created + result.suggestions.length;
      if (totalFound > 0) {
        confirmDialog({
          message: (
            <div>
              <p><strong>{result.allocations_created}</strong> automated match{result.allocations_created !== 1 ? 'es' : ''} created</p>
              <p><strong>{result.suggestions.length}</strong> suggestion{result.suggestions.length !== 1 ? 's' : ''} for review</p>
            </div>
          ),
          header: 'Allocations Found',
          icon: 'ms-out ms-out-check-circle icon-success',
          acceptLabel: 'OK',
          acceptClassName: 'p-button-success',
          rejectClassName: 'hidden',
        });
      } else {
        confirmDialog({
          message: 'No allocations found with the current parameters. Try adjusting the tolerance or date window.',
          header: 'No Allocations Found',
          icon: 'ms-out ms-out-info icon-info',
          acceptLabel: 'OK',
          acceptClassName: 'p-button-success',
          rejectClassName: 'hidden',
        });
      }

      if (result.suggestions.length > 0) {
        onSwitchToSuggestions();
      }
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Failed to run allocation engine';
      setLocalError(message);
      onError(message);
    } finally {
      setIsRunning(false);
    }
  };

  useImperativeHandle(ref, () => ({
    runAllocations,
  }));

  const handleAmountToleranceChange = (value: number | null | undefined) => {
    const next = value ?? 1.0;
    setAmountTolerance(next);
  };

  const handleDateWindowChange = (value: number | null | undefined) => {
    const next = value ?? 3;
    setDateWindow(next);
  };

  const handleMaxClusterSizeChange = (value: number | null | undefined) => {
    const next = value ?? 3;
    setMaxClusterSize(next);
  };

  return (
    <>
      {localError && <Message severity="error" text={localError} />}
      <Menubar
        className="actions-menubar"
        model={[
          accountMenuItem,
          {
            label: isRunning ? 'Finding...' : 'Find Allocations',
            icon: isRunning ? 'pi pi-spin pi-spinner' : 'ms-out ms-out-wand-shine',
            command: runAllocations,
            disabled: isRunning,
          },
          {
            template: () => (
              <div className="menu-item-controls-group">
                <div className="menu-item-control">
                  <label htmlFor="amountTolerance">tolerance ($):</label>
                  <InputNumber
                    id="amountTolerance"
                    value={amountTolerance}
                    onValueChange={(e) => handleAmountToleranceChange(e.value)}
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    showButtons
                    buttonLayout="horizontal"
                    decrementButtonClassName="controls-panel-item-button"
                    incrementButtonClassName="controls-panel-item-button"
                    incrementButtonIcon="ms-out ms-out-add"
                    decrementButtonIcon="ms-out ms-out-remove"
                    min={0}
                    disabled={isRunning}
                  />
                </div>
                <div className="menu-item-control">
                  <label htmlFor="dateWindow">window (days):</label>
                  <InputNumber
                    id="dateWindow"
                    value={dateWindow}
                    onValueChange={(e) => handleDateWindowChange(e.value)}
                    min={0}
                    max={30}
                    showButtons
                    buttonLayout="horizontal"
                    decrementButtonClassName="controls-panel-item-button"
                    incrementButtonClassName="controls-panel-item-button"
                    incrementButtonIcon="ms-out ms-out-add"
                    decrementButtonIcon="ms-out ms-out-remove"
                    disabled={isRunning}
                  />
                  <label htmlFor="maxClusterSize">max cluster:</label>
                </div>
                <div className="menu-item-control">
                  <InputNumber
                    id="maxClusterSize"
                    value={maxClusterSize}
                    onValueChange={(e) => handleMaxClusterSizeChange(e.value)}
                    min={1}
                    max={10}
                    showButtons
                    buttonLayout="horizontal"
                    decrementButtonClassName="controls-panel-item-button"
                    incrementButtonClassName="controls-panel-item-button"
                    incrementButtonIcon="ms-out ms-out-add"
                    decrementButtonIcon="ms-out ms-out-remove"
                    disabled={isRunning}
                  />
                </div>
              </div>
            ),
          },
        ]}
      />
    </>
  );
});

export default AllocationControls;
